/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { Fragment, useState, useEffect, useCallback } from 'react'
import { jsx } from '@emotion/react'
import { defineMessages, useIntl } from 'react-intl'
import { intersectionWith } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiTitle, EuiSpacer, EuiText, EuiToolTip } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  Allocator,
  AllocatorInstance,
  VacateResult as VacateResultType,
} from '@modules/ui-types'
import { CuiBasicFilterContext } from '@modules/cui/BasicFilterContext'
import { CuiButton } from '@modules/cui/Button'
import { CuiAlert } from '@modules/cui/Alert'
import type { CuiBasicFilterContextProps } from '@modules/cui/BasicFilterContext'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'

import AllocatorInstancesTable from '../AllocatorInstancesTable'

import { getFilters } from './filters'
import VacateResult from './VacateResult'
import VacateNodesModal from './VacateNodesModal'

export type Props = {
  allocator: Allocator
  vacateResult?: VacateResultType
}

const messages = defineMessages({
  selectAnInstance: {
    id: 'allocator-instances-move.select-an-instance',
    defaultMessage: 'Select an instance to move it to a different allocator',
  },
})

const VacateAllocator = ({ allocator, vacateResult }: Props) => {
  const [selectedInstances, setSelectedInstances] = useState<AllocatorInstance[]>([])
  const [showOptionsModal, setShowOptionsModal] = useState(false)
  const intl = useIntl()

  // Whenever there's an allocator change, make sure we
  // don't have nodes chosen that no longer live on
  // this allocator.
  useEffect(() => {
    setSelectedInstances((prevSelected) =>
      intersectionWith<AllocatorInstance, AllocatorInstance>(
        prevSelected,
        allocator.instances,
        (left, right) => left.id === right.id,
      ),
    )
  }, [allocator])

  const showOptionsModalHandler = useCallback(() => {
    setShowOptionsModal(true)
  }, [])

  const closeOptionsModal = useCallback(({ clearSelection }: { clearSelection: boolean }) => {
    setShowOptionsModal(false)

    if (clearSelection) {
      setSelectedInstances([])
    }
  }, [])

  const selectInstances = useCallback((instances: AllocatorInstance[]) => {
    setSelectedInstances(instances)
  }, [])

  const renderInstancesTable = useCallback(
    ({
      filteredInstances,
      unfilteredInstances,
    }: {
      filteredInstances?: AllocatorInstance[]
      unfilteredInstances: AllocatorInstance[]
    }) => {
      const { regionId } = allocator

      return (
        <AllocatorInstancesTable
          regionId={regionId}
          instances={filteredInstances}
          selectedInstances={selectedInstances}
          selectInstances={selectInstances}
          unfilteredInstances={unfilteredInstances}
          totalCount={unfilteredInstances ? unfilteredInstances.length : undefined}
        />
      )
    },
    [allocator, selectedInstances, selectInstances],
  )

  const renderMoveInstancesButton = useCallback(() => {
    const { formatMessage } = intl
    const selectedAnyInstances = selectedInstances.length > 0

    const MoveInstancesButton = () => (
      <PermissionsGate
        permissions={[
          {
            type: 'allocator',
            action: 'move',
            id: allocator.id,
          },
        ]}
      >
        {({ hasPermissions }) => (
          <CuiButton
            disabled={!selectedAnyInstances || !hasPermissions}
            onClick={showOptionsModalHandler}
            data-test-id='move-node-button'
          >
            <FormattedMessage
              id='allocator-vacate.move-instances-configure'
              defaultMessage='Move instances'
            />
          </CuiButton>
        )}
      </PermissionsGate>
    )

    return !selectedAnyInstances ? (
      <EuiToolTip content={formatMessage(messages.selectAnInstance)} position='bottom'>
        <MoveInstancesButton />
      </EuiToolTip>
    ) : (
      <MoveInstancesButton />
    )
  }, [intl, selectedInstances.length, allocator.id, showOptionsModalHandler])

  const renderAllocatorInstancesFilterContext = () => {
    const supportedTypes = getSupportedSliderInstanceTypes()
    const allocatorInstances = allocator.instances.filter((instance) =>
      supportedTypes.includes(instance.kind),
    )

    if (allocatorInstances.length === 0) {
      return (
        <CuiAlert type='info'>
          <FormattedMessage
            id='allocator-overview.no-nodes-running-on-this-allocator'
            defaultMessage='There are no nodes running on this allocator'
          />
        </CuiAlert>
      )
    }

    const schemaFields: CuiBasicFilterContextProps<AllocatorInstance>['schemaFields'] = [
      'id',
      { id: 'healthy', type: 'boolean' },
      { id: 'type', aliasOf: 'kind' },
      { id: 'name', aliasOf: 'instanceName' },
      { id: 'deployment', aliasOf: 'stackDeploymentId' },
      { id: 'cluster', aliasOf: 'clusterId' },
      { id: 'cluster_name', aliasOf: 'clusterDisplayName' },
      { id: 'healthy_cluster', aliasOf: 'clusterHealthy', type: 'boolean' },
    ]

    const schemaDefaultFields: CuiBasicFilterContextProps<AllocatorInstance>['schemaDefaultFields'] =
      [`id`, `type`, `name`, `deployment`, `cluster`, `cluster_name`]

    const filters = getFilters({
      intl,
    })

    return (
      <CuiBasicFilterContext<AllocatorInstance>
        rows={allocatorInstances}
        schemaFields={schemaFields}
        schemaDefaultFields={schemaDefaultFields}
        filters={filters}
        actions={renderMoveInstancesButton()}
      >
        {(filteredInstances?: AllocatorInstance[]) =>
          renderInstancesTable({ filteredInstances, unfilteredInstances: allocatorInstances })
        }
      </CuiBasicFilterContext>
    )
  }

  const renderMoveNodesOptionsModal = () => {
    if (!showOptionsModal) {
      return null
    }

    return (
      <VacateNodesModal allocator={allocator} nodes={selectedInstances} close={closeOptionsModal} />
    )
  }

  const { regionId, instances } = allocator

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiTitle size='s'>
            <h5>
              <FormattedMessage
                id='allocator-overview.instances-label'
                defaultMessage='Instances'
              />
            </h5>
          </EuiTitle>
        </EuiFlexItem>

        {instances.length && (
          <EuiFlexItem grow={false}>
            <EuiText color='subdued'>{instances.length}</EuiText>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      <EuiSpacer size='l' />

      {renderAllocatorInstancesFilterContext()}

      <VacateResult regionId={regionId} vacateResult={vacateResult} />

      {renderMoveNodesOptionsModal()}
    </Fragment>
  )
}

export default VacateAllocator
